// Domain-owned route containers. Do not import legacy connected-container or all-domain registries here.
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { EvidenceItem, WorkflowCommand } from "@swot/swot-components";
import { readCmtsSession } from "../../auth/authStore.js";
import { resolveCurrentCmtsSession } from "../../session/cmtsSessionResolver.js";
import { useCmtsDispatch } from "@store/hooks.js";
import { authActions } from "@store/authSlice.js";
import { permissionActions } from "@store/permissionSlice.js";
import { tenantActions } from "@store/tenantSlice.js";
import { serializeReduxError } from "@store/thunkExtra.js";
import { useCmtsAuth } from "../../auth/authHooks.js";
import { useComplaintRegister } from "@hooks/useComplaintRegister.js";
import { useComplaintDetail } from "@hooks/useComplaintDetail.js";
import { useComplaintWorkflow } from "@hooks/useComplaintWorkflow.js";
import { useEvidence } from "@hooks/useEvidence.js";
import { cmtsKeys } from "@hooks/cmtsQueryKeys.js";
import type { RepositoryResult } from "../../repositories/repositoryResult.js";
import type { CmtsHostRuntime } from "../../../../app/appRuntime.js";
import type { CmtsComplaintDetailVm, CmtsComplaintRecordVm } from "../../view-models/complaintViewModels.js";
import type { CmtsConfigurationDraftVm, CmtsConfigurationVersionVm } from "../../mappers/configurationMappers.js";
import { queryObjectFromSearch } from "../../filters/cmtsFilterEngine.js";
import { decideCmtsExport, createCmtsExportAuditPayload } from "../../export/cmtsSecureExport.js";
import { getCmtsPersonaLandingPath } from "../../routes/cmtsPersonaLanding.js";
import { normalizeTypedReportCode, reportCodeFromPathname } from "../../reporting/cmtsReportConfig.js";
import { throwCmtsCanonicalError } from "../../api/cmtsApiErrors.js";
import { cmtsMasterDataEntityForPathSegment } from "../../master-data/cmtsEnterpriseMasterDataRegistry.js";
import { activeRows, assertRepositoryOk, businessValueOptionOf, complaintQuery, dataOr, diagnosticsOf, emptyBackendDashboard, errorOf, invalidateAdminArea, loadMasterOptions, optionOf, tenantId, useAdminDependencyData, useCmtsRouterSearch, type CmtsConnectedContainerProps } from "../domainContainerSupport.js";
import { CmtsDashboardsPage } from "@modules/pages/cmtsDashboardsPage.js";
import type { DashboardType } from "@modules/core/repositories/dashboardRepository.js";
function resolveDashboardType(
  pathname: string,
): DashboardType {
  const lastSegment =
    pathname
      .split("/")
      .filter(Boolean)
      .at(-1)
      ?.trim()
      .toLowerCase()
      .replace(/-dashboard$/, "") ??
    "management";

  switch (lastSegment) {
    case "management":
    case "quality":
    case "department":
    case "personal":
    case "customer":
    case "supplier":
      return lastSegment;

    case "dashboards":
    default:
      return "management";
  }
}
export function CmtsDashboardsContainer({
  runtime,
}: CmtsConnectedContainerProps) {
  const t = tenantId(runtime);

  const resolvedSession =
    resolveCurrentCmtsSession();

  const sessionKey =
    resolvedSession.status === "ready"
      ? resolvedSession.session.userId
      : "anonymous";

  const location = useLocation();
  const navigate = useNavigate();

  const dashboardType =
    resolveDashboardType(
      location.pathname,
    );

  const dashboard = useQuery({
    /*
     * cmtsKeys.dashboards helper-ல் Supplier/Customer
     * support இல்லாவிட்டாலும் query crash ஆகக்கூடாது.
     */
    queryKey: [
      "cmts",
      "dashboards",
      dashboardType,
      t ?? "no-tenant",
      sessionKey,
      location.pathname,
    ],

    queryFn: () =>
      runtime.repositories.dashboards.get(
        dashboardType,
        {
          tenantId: t,
          sessionKey,
          dashboardRoute:
            location.pathname,
        },
      ),

    enabled: Boolean(t),
    retry: 1,
  });

  const drilldown = useMutation({
    mutationFn: (id: string) =>
      runtime.repositories.dashboards
        .drilldown({
          id,
          tenantId: t,
          dashboardType,
        }),
  });

  /*
   * RepositoryResult failure-ஐ empty dashboard-ஆக
   * மாற்றக்கூடாது. Actual diagnostics preserve
   * செய்ய வேண்டும்.
   */
  const repositoryFailure =
    dashboard.data &&
    !dashboard.data.ok
      ? dashboard.data.diagnostics
      : undefined;

  const dashboardFailure =
    dashboard.error ??
    repositoryFailure;

  const dashboardValue =
    dashboard.data?.ok
      ? dashboard.data.data
      : null;

  const effectiveDashboard =
    dashboardValue ??
    (
      dashboardFailure
        ? null
        : emptyBackendDashboard(
            dashboardType,
            t,
          )
    );

  return (
    <CmtsDashboardsPage
      dashboardType={dashboardType}
      dashboard={effectiveDashboard}
      error={
        dashboard.isLoading
          ? undefined
          : dashboardFailure
      }
      drilldown={dataOr(
        drilldown.data,
        null,
      )}
      onDrilldown={(id) =>
        drilldown.mutate(id)
      }
      onOpenDrilldown={(path) =>
        navigate(path)
      }
    />
  );
}